import { useCallback, useState } from 'react'
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Dialog, HelperText, Text, useTheme } from 'react-native-paper'
import { useJobLocations } from '@/hooks/locations'
import { PickerDialog } from '@/ui/PickerDialog'

interface LocationPickerProps<T extends FieldValues = Record<string, unknown>> {
    name: Path<T>
    control: Control<T>
    jobId: string
    label?: string
}

export function LocationPicker<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    jobId,
    label = 'Location (optional)',
}: LocationPickerProps<T>) {
    const [visible, setVisible] = useState(false)
    const theme = useTheme()
    const dismissDialog = useCallback(() => setVisible(false), [])

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    const { value, onChange } = field

    const { locations = [] } = useJobLocations(jobId)

    const selectedLocation = locations.find(l => l.id === value)

    if (locations.length === 0) {
        return null
    }

    return (
        <View style={styles.wrapper}>
            <TouchableOpacity
                onPress={() => setVisible(true)}
                style={[
                    styles.picker,
                    {
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.outline,
                    },
                ]}
            >
                <View style={styles.pickerContent}>
                    <Text
                        variant="bodySmall"
                        style={[styles.label, { color: theme.colors.onSurfaceVariant }]}
                    >
                        {label}
                    </Text>
                    <Text
                        variant="bodyLarge"
                        style={
                            value
                                ? { color: theme.colors.onSurface }
                                : { color: theme.colors.onSurfaceVariant }
                        }
                    >
                        {selectedLocation?.name || 'Select a location'}
                    </Text>
                </View>
            </TouchableOpacity>
            {error ? <HelperText type="error">{error.message}</HelperText> : null}

            <PickerDialog visible={visible} onDismiss={dismissDialog}>
                <Dialog.Title style={{ color: theme.colors.onSurface }}>
                    Select Location
                </Dialog.Title>
                <Dialog.Content>
                    <FlatList
                        data={[{ id: '', name: 'None' }, ...locations]}
                        keyExtractor={item => item.id || 'none'}
                        style={styles.list}
                        renderItem={({ item }) => (
                            <TouchableOpacity
                                style={styles.item}
                                onPress={() => {
                                    onChange(item.id)
                                    setVisible(false)
                                }}
                            >
                                <Text
                                    variant="bodyLarge"
                                    style={[styles.itemText, { color: theme.colors.onSurface }]}
                                >
                                    {item.name}
                                </Text>
                            </TouchableOpacity>
                        )}
                    />
                </Dialog.Content>
            </PickerDialog>
        </View>
    )
}

const styles = StyleSheet.create({
    wrapper: {
        marginBottom: 8,
    },
    picker: {
        borderWidth: 1,
        borderRadius: 4,
        padding: 12,
    },
    pickerContent: {
        gap: 4,
    },
    label: {},
    list: {
        maxHeight: 300,
    },
    item: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 4,
    },
    itemText: {
        flex: 1,
    },
})
